import ExclamationTriangleIcon from '@heroicons/react/24/outline/ExclamationTriangleIcon';
import LockClosedIcon from '@heroicons/react/24/outline/LockClosedIcon';
import PhotoIcon from '@heroicons/react/24/outline/PhotoIcon';
import { ComponentPropsWithoutRef, ComponentType } from 'react';

import { Button } from '@/components/core/Button';
import { Icon } from '@/components/core/Icon';
import { useTranslation } from '@/hooks/useTranslation';
import { navigateTo } from '@/services/angularjs-bridge';

type DamLibraryEmptyStateVariant = 'noAssets' | 'noResult' | 'error' | 'noAccess' | 'notFound';

interface DamLibraryEmptyStateProps {
    variant: DamLibraryEmptyStateVariant;
    /** "Add new files" (no assets); disabled while no handler is wired. */
    onAddFiles?: () => void;
    /** "Start a new search" (no result): clears search and filters. */
    onNewSearch?: () => void;
    /** "Adjust filter" (no result); disabled while no handler is wired. */
    onAdjustFilter?: () => void;
    /** "Try again" (error). */
    onRetry?: () => void;
    /** Target of "Back to the dashboard" (error, no access). */
    dashboardHref?: string;
    /** "Back to the asset library" (detail page: asset not found). */
    onBackToLibrary?: () => void;
}

const ICONS: Record<DamLibraryEmptyStateVariant, ComponentType<ComponentPropsWithoutRef<'svg'>>> = {
    noAssets: PhotoIcon,
    noResult: PhotoIcon,
    error: ExclamationTriangleIcon,
    noAccess: LockClosedIcon,
    notFound: PhotoIcon,
};

const TEXT_KEYS: Record<DamLibraryEmptyStateVariant, { title: string; text: string }> = {
    noAssets: { title: 'DAM.LIBRARY.EMPTY.NO_ASSETS_TITLE', text: 'DAM.LIBRARY.EMPTY.NO_ASSETS_TEXT' },
    noResult: { title: 'DAM.LIBRARY.EMPTY.NO_RESULT_TITLE', text: 'DAM.LIBRARY.EMPTY.NO_RESULT_TEXT' },
    error: { title: 'DAM.LIBRARY.EMPTY.ERROR_TITLE', text: 'DAM.LIBRARY.EMPTY.ERROR_TEXT' },
    noAccess: { title: 'DAM.LIBRARY.EMPTY.NO_ACCESS_TITLE', text: 'DAM.LIBRARY.EMPTY.NO_ACCESS_TEXT' },
    notFound: { title: 'DAM.DETAIL.NOT_FOUND_TITLE', text: 'DAM.DETAIL.NOT_FOUND_TEXT' },
};

/**
 * Empty and error surfaces of the library (wireframe states No Assets `6392:31623`,
 * No result `6392:31624`, Error `6392:31625`, No access `6392:31626`); the detail page
 * reuses error and no access and adds "not found".
 */
function DamLibraryEmptyState({
    variant,
    onAddFiles,
    onNewSearch,
    onAdjustFilter,
    onRetry,
    dashboardHref = '/',
    onBackToLibrary,
}: DamLibraryEmptyStateProps) {
    const { t } = useTranslation();
    const keys = TEXT_KEYS[variant];
    const backToDashboard = (
        <Button variant="outline" onClick={() => navigateTo(dashboardHref)}>
            {t('DAM.LIBRARY.EMPTY.BACK_TO_DASHBOARD')}
        </Button>
    );

    return (
        <section className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-px-4 tw-py-16 tw-text-center" aria-labelledby={`dam-empty-${variant}`}>
            <Icon icon={ICONS[variant]} size="xl" variant="lighter-gray" />
            <h2 id={`dam-empty-${variant}`} className="tw-m-0 tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">
                {t(keys.title)}
            </h2>
            <p className="tw-m-0 tw-max-w-md tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t(keys.text)}</p>
            <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
                {variant === 'noAssets' && (
                    <Button disabled={!onAddFiles} onClick={onAddFiles}>{t('DAM.ADD_NEW_FILES')}</Button>
                )}
                {variant === 'noResult' && (
                    <>
                        <Button disabled={!onNewSearch} onClick={onNewSearch}>{t('DAM.LIBRARY.EMPTY.NEW_SEARCH')}</Button>
                        <Button variant="outline" disabled={!onAdjustFilter} onClick={onAdjustFilter}>{t('DAM.LIBRARY.EMPTY.ADJUST_FILTER')}</Button>
                    </>
                )}
                {variant === 'error' && (
                    <>
                        <Button disabled={!onRetry} onClick={onRetry}>{t('DAM.LIBRARY.EMPTY.TRY_AGAIN')}</Button>
                        {backToDashboard}
                    </>
                )}
                {variant === 'noAccess' && backToDashboard}
                {variant === 'notFound' && (
                    <Button disabled={!onBackToLibrary} onClick={onBackToLibrary}>{t('DAM.DETAIL.BACK_TO_LIBRARY')}</Button>
                )}
            </div>
        </section>
    );
}

export { DamLibraryEmptyState };
export type { DamLibraryEmptyStateProps, DamLibraryEmptyStateVariant };
